import React, { useState, useEffect, useRef } from "react";
import T from '../../components/common/T';
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTasks } from "../../context/TaskContext";
import { useTimesheets } from "../../context/TimesheetContext"; 
import { useDocuments } from "../../context/DocumentContext"; 
import { useEmployees } from "../../context/EmployeeContext"; 
import { useProjects } from "../../context/ProjectContext";
import { useCostSheets } from "../../context/CostSheetContext"; 
import Price from "../../components/common/Price";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL, default as api } from "../../api/axiosConfig";
import { useCustomFields } from '../../hooks/useCustomFields';
import CustomFieldRenderer from '../../components/common/CustomFieldRenderer';
import { formatDate } from '../../utils/dateUtils';
import { 
  ClockIcon, 
  CalendarDaysIcon, 
  ArrowLeftIcon,
  PaperClipIcon,
  TrashIcon,
  DocumentIcon,
  UserCircleIcon,
  CurrencyRupeeIcon
} from "@heroicons/react/24/outline";

export default function TaskDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  
  // --- CONTEXTS ---
  const { getTaskById, fetchTaskById, deleteTask: contextDeleteTask, loading: contextLoading } = useTasks();
  const { addLog, deleteLog, getTaskLogs, getTaskTotalHours, fetchTaskLogs } = useTimesheets();
  const { addDocument, getTaskDocuments, deleteDocument, fetchTaskDocuments } = useDocuments();
  const { employees } = useEmployees();
  const { projects } = useProjects();
  const { getProjectCostSheets, addCostLabor } = useCostSheets(); 

  // --- STATE ---
  const [task, setTask] = useState(null);
  const [isSyncing, setIsSyncing] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const loadedIdRef = useRef(null);
  const [activeTab, setActiveTab] = useState(() => {
    if (urlTab === "timesheets" || urlTab === "daily-logs" || urlTab === "daily-log" || urlTab === "logs") {
      return "timesheets";
    }
    return urlTab || "overview";
  });
  const { customData, setCustomData } = useCustomFields('tasks', id || null);

  useEffect(() => {
    if (urlTab === "timesheets" || urlTab === "daily-logs" || urlTab === "daily-log" || urlTab === "logs") {
      setActiveTab("timesheets");
    } else if (urlTab) {
      setActiveTab(urlTab);
    }
  }, [urlTab]);
  
  const [logData, setLogData] = useState({
      hours: "",
      description: "",
      costSheetId: "", 
      date: new Date().toISOString().split('T')[0]
  });

  // 🟢 DEDICATED VIEW API: Always fetches fresh single task directly from GET /api/tasks/:id (Single execution guard)
  useEffect(() => {
    if (!id) return;
    if (loadedIdRef.current === String(id)) return;
    loadedIdRef.current = String(id);

    let isMounted = true;
    const loadTaskDetails = async () => {
      setIsSyncing(true);
      try {
        const res = await api.get(`/tasks/${id}`);
        const fetched = res.data?.data || res.data?.task || res.data;
        if (isMounted && fetched) {
          // Parse measurement and rate if embedded in description string
          let parsedQuantity = fetched.quantity;
          let parsedUnit = fetched.unit;
          let parsedUnitRate = fetched.unitRate;
          let parsedNotes = fetched.notes;

          if (fetched.description && fetched.description.includes('[Details:')) {
            const match = fetched.description.match(/\[Details:\s*(.*?)\]/);
            if (match && match[1]) {
              const parts = match[1].split('|').map(s => s.trim());
              parts.forEach(p => {
                if (p.startsWith('Measurement:')) {
                  const m = p.replace('Measurement:', '').trim().split(' ');
                  if (!parsedQuantity) parsedQuantity = m[0];
                  if (!parsedUnit) parsedUnit = m.slice(1).join(' ');
                } else if (p.startsWith('Rate:')) {
                  if (!parsedUnitRate) parsedUnitRate = p.replace('Rate:', '').replace('₹', '').trim();
                } else if (p.startsWith('Notes:')) {
                  if (!parsedNotes) parsedNotes = p.replace('Notes:', '').trim();
                }
              });
            }
          }

          setTask({
            ...fetched,
            quantity: parsedQuantity || fetched.quantity || "",
            unit: parsedUnit || fetched.unit || "",
            unitRate: parsedUnitRate || fetched.unitRate || "",
            notes: parsedNotes || fetched.notes || "",
            totalCost: fetched.totalCost || fetched.cost || 0
          });
        }
      } catch (err) {
        console.error("Dedicated GET /api/tasks/:id error:", err);
        toast.error("Failed to fetch task details from server");
        if (isMounted) setTask(null);
      } finally {
        if (isMounted) setIsSyncing(false);
      }
    };

    loadTaskDetails();
    if (fetchTaskLogs) fetchTaskLogs(id);
    if (fetchTaskDocuments) fetchTaskDocuments(id);
    return () => { isMounted = false; };
  }, [id, fetchTaskLogs, fetchTaskDocuments]);

  // Loading indicator
  if (isSyncing) return (
    <div className="p-16 text-center text-gray-500 dark:text-slate-400">
      <div className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-3"></div>
      <p className="text-sm font-medium"><T>Loading task details from server...</T></p>
    </div>
  );

  if (!task) return (
    <div className="p-10 text-center text-gray-500 dark:text-slate-400">
        <T>Task not found</T>
        <button onClick={() => navigate("/admin/task")} className="mt-4 block mx-auto text-blue-600 dark:text-blue-400 hover:underline"><T>Back to list</T></button>
    </div>
  );

  // --- DERIVED DATA ---
  const projectId = task.projectId || task.project_id;
  const projectSheets = getProjectCostSheets(projectId); 
  const project = projects?.find(p => String(p.id) === String(projectId));
  const displayProjectName = task.projectName || project?.name || "General Project";
  const displayProjectLocation = task.projectLocation || project?.location || "";
  const displayProjectClient = task.projectClient || project?.client_name || "";
  const displayProjectStage = task.projectStage || project?.stage || "";

  // 🟢 Unified Assignee Resolution (Backend enriched object has priority, then context fallback)
  const backendAssignee = task.assignedEmployee;
  const contextEmp = employees.find(e => {
      if (task.assignedEmployeeId && String(e.id) === String(task.assignedEmployeeId)) return true;
      if (task.assigned_to && String(e.id) === String(task.assigned_to)) return true;
      if (task.employeeId && String(e.id) === String(task.employeeId)) return true;
      if (task.assignedTo && (e.employeeName === task.assignedTo || e.name === task.assignedTo)) return true;
      return false;
  });

  const displayEmployeeName = (backendAssignee && (backendAssignee.name || backendAssignee.employeeName))
    || (task.assignedName && !task.assignedName.startsWith("Employee #") ? task.assignedName : null)
    || (task.assignedTo && !task.assignedTo.startsWith("Employee #") ? task.assignedTo : null)
    || (contextEmp && (contextEmp.employeeName || contextEmp.name))
    || "Unassigned";

  const displayEmployeeRole = backendAssignee?.role || backendAssignee?.trade || task.assignedRole || contextEmp?.role || contextEmp?.trade || "Staff";
  const displayEmployeePhone = backendAssignee?.phone || contextEmp?.phone || "";
  const displayEmployeeEmail = backendAssignee?.email || contextEmp?.email || "";
  const hourlyRate = (backendAssignee && Number(backendAssignee.hourlyRate))
    || (contextEmp && Number(contextEmp.hourlyRate))
    || Number(task.hourlyRate)
    || 0;

  // Strict check: Is a real team member assigned?
  const isTaskAssigned = Boolean(
    (task.assigned_to || task.assignedEmployeeId || task.employeeId) &&
    displayEmployeeName !== "Unassigned" &&
    !displayEmployeeName.startsWith("Employee #")
  );

  const logs = getTaskLogs(id);
  const taskDocs = getTaskDocuments ? getTaskDocuments(id) : [];
  const totalSpentHours = getTaskTotalHours(id);
  const estimatedHours = Number(task.estimatedHours) || 0;
  const progress = estimatedHours > 0 ? Math.min((totalSpentHours / estimatedHours) * 100, 100) : (task.progress || 0);

  const totalTaskValue = Number(task.totalCost || task.cost || 0);
  const estLaborCost = estimatedHours * hourlyRate;
  const incurredLaborCost = totalSpentHours * hourlyRate;

  // --- HANDLERS ---
  const handleLogSubmit = async (e) => { 
      e.preventDefault();
      if (!isTaskAssigned) {
        return toast.error("Hours cannot be logged for unassigned tasks. Please assign a team member first.");
      }
      if (!logData.hours || !logData.description) return toast.warn("Please fill all required fields");
      const hours = Number(logData.hours);
      if (isNaN(hours) || hours <= 0) return toast.warn("Please enter a valid number of hours");
      const cost = hours * hourlyRate;

      const employeeIdToLog = backendAssignee?.id || contextEmp?.id || task.assigned_to || task.assignedEmployeeId || 1;

      await addLog({
          taskId: id,
          taskName: task.title,
          projectId: projectId,
          projectName: displayProjectName,
          employeeId: employeeIdToLog, 
          employeeName: displayEmployeeName,
          costSheetId: logData.costSheetId || null, 
          rate: hourlyRate, 
          cost: cost,        
          hours: hours,
          description: logData.description,
          date: logData.date
      });

      if (logData.costSheetId) {
          await addCostLabor({
              costSheetId: logData.costSheetId,
              projectId: projectId,
              employeeName: displayEmployeeName,
              description: logData.description,
              hours: hours,
              hourlyRate: hourlyRate,
              totalCost: cost,
              date: logData.date
          });
          toast.success("Cost synchronized with project Cost Sheet");
      } else {
          toast.success("Work hours logged successfully");
      }
      setLogData({ ...logData, hours: "", description: "", costSheetId: "" });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("taskId", id);
    if (projectId) formData.append("projectId", projectId);
    formData.append("documentName", file.name);
    try {
      await addDocument(formData);
      toast.success("Document uploaded successfully");
      e.target.value = "";
    } catch (err) {
      console.error("Document upload failed:", err);
    }
  };

  const handleDeleteTask = async () => {
    if (!window.confirm("Are you sure you want to permanently delete this task?")) return;
    setIsDeleting(true);
    try {
      if (contextDeleteTask) {
        await contextDeleteTask(id);
      } else {
        await api.delete(`/tasks/${id}`);
        toast.success("Task deleted successfully");
      }
      navigate("/admin/task");
    } catch (err) {
      console.error("Failed to delete task:", err);
      toast.error(err.response?.data?.error || "Failed to delete task");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="p-6 bg-gray-50 dark:bg-[#0d1117] min-h-screen font-sans">
        
        {/* Navigation & Actions Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
                <button onClick={() => navigate("/admin/task")} className="flex items-center text-xs font-semibold text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-white mb-2 transition">
                    <ArrowLeftIcon className="w-3.5 h-3.5 mr-1" /> Back to Tasks
                </button>
                <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">{task.title}</h1>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide border
                        ${task.priority === 'Urgent' || task.priority === 'High' ? 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-900/50' : 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-900/50'}`}>
                        {task.priority}
                    </span>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 border border-gray-200 dark:border-slate-700">
                        {task.status}
                    </span>
                </div>
            </div>
            <div className="flex items-center gap-2">
                <button onClick={() => navigate(`/admin/task/edit/${id}`)} className="bg-white dark:bg-[#161b22] border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-slate-200 px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-[#21262d] text-xs font-bold uppercase tracking-wider shadow-xs transition">
                    <T>Edit Task</T>
                </button>
                <button 
                  onClick={handleDeleteTask} 
                  disabled={isDeleting}
                  className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400 px-4 py-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/60 text-xs font-bold uppercase tracking-wider shadow-xs transition flex items-center gap-1.5 disabled:opacity-50"
                >
                    <TrashIcon className="w-4 h-4" /> <T>{isDeleting ? "Deleting..." : "Delete"}</T>
                </button>
            </div>
        </div>

        {/* 🟢 INTERCONNECTIVITY KPI SUMMARY STRIP */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            
            {/* 1. Project Interconnection Card */}
            <div className="bg-white dark:bg-[#161b22] p-4 rounded-xl border border-gray-200 dark:border-[#30363d] shadow-xs hover:border-blue-300 dark:hover:border-blue-800 transition">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider">Linked Project</span>
                    {projectId && (
                      <button 
                        onClick={() => navigate(`/admin/project/${projectId}`)} 
                        className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline font-semibold"
                      >
                        View Project →
                      </button>
                    )}
                </div>
                <button 
                  onClick={() => projectId && navigate(`/admin/project/${projectId}`)}
                  className="text-left font-bold text-gray-900 dark:text-white text-sm hover:text-blue-600 dark:hover:text-blue-400 transition truncate block w-full"
                >
                  📁 {displayProjectName}
                </button>
                <div className="mt-2 text-[11px] text-gray-500 dark:text-slate-400 space-y-0.5">
                    {displayProjectLocation && <p className="truncate">📍 {displayProjectLocation}</p>}
                    {displayProjectClient && <p className="truncate">🏢 Client: {displayProjectClient}</p>}
                </div>
            </div>

            {/* 2. Assigned Team Member Card */}
            <div className="bg-white dark:bg-[#161b22] p-4 rounded-xl border border-gray-200 dark:border-[#30363d] shadow-xs">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider">Assigned Team Member</span>
                    <button 
                      onClick={() => navigate(`/admin/task/edit/${id}`)}
                      className="text-[10px] text-indigo-600 dark:text-indigo-400 hover:underline font-semibold"
                    >
                      {isTaskAssigned ? "Change" : "+ Assign"}
                    </button>
                </div>
                {isTaskAssigned ? (
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 font-bold text-xs flex items-center justify-center shrink-0 border border-blue-200 dark:border-blue-800">
                      {displayEmployeeName.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-gray-900 dark:text-white text-sm truncate">{displayEmployeeName}</p>
                      <p className="text-[11px] text-gray-500 dark:text-slate-400 truncate">{displayEmployeeRole}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 text-xs font-semibold py-1">
                    <span>⚠️ Unassigned</span>
                    <span className="text-[10px] font-normal text-gray-400">(Hour logging locked)</span>
                  </div>
                )}
                <div className="mt-2 pt-2 border-t border-gray-100 dark:border-[#30363d] flex justify-between items-center text-[11px]">
                  <span className="text-gray-400">Hourly Rate:</span>
                  <span className="font-bold text-gray-800 dark:text-slate-200"><Price amount={hourlyRate} />/hr</span>
                </div>
            </div>

            {/* 3. Time & Effort Card */}
            <div className="bg-white dark:bg-[#161b22] p-4 rounded-xl border border-gray-200 dark:border-[#30363d] shadow-xs">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider">Time Tracked</span>
                    <span className={`text-xs font-bold ${progress >= 100 ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}`}>
                      {Math.round(progress)}%
                    </span>
                </div>
                <div className="flex items-baseline gap-1.5">
                    <span className="text-xl font-bold text-gray-900 dark:text-white">{totalSpentHours}h</span>
                    <span className="text-xs text-gray-500 dark:text-slate-400">logged of {estimatedHours}h est.</span>
                </div>
                <div className="w-full bg-gray-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden mt-3">
                    <div className={`h-full transition-all duration-1000 ${progress >= 100 ? 'bg-green-500' : 'bg-blue-600'}`} style={{ width: `${progress}%` }}></div>
                </div>
            </div>

            {/* 4. Financial Valuation Card */}
            <div className="bg-white dark:bg-[#161b22] p-4 rounded-xl border border-gray-200 dark:border-[#30363d] shadow-xs">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider">Task Valuation</span>
                    <span className="text-[10px] text-purple-600 dark:text-purple-400 font-bold uppercase">BOQ Value</span>
                </div>
                <p className="text-xl font-bold text-purple-700 dark:text-purple-400">
                  <Price amount={totalTaskValue} />
                </p>
                <div className="mt-2 pt-2 border-t border-gray-100 dark:border-[#30363d] flex justify-between items-center text-[11px]">
                  <span className="text-gray-400">Incurred Labor:</span>
                  <span className="font-semibold text-gray-800 dark:text-slate-200"><Price amount={incurredLaborCost} /></span>
                </div>
            </div>

        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-gray-200 dark:border-[#30363d] mb-6">
            <button onClick={() => setActiveTab("overview")} className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${activeTab === 'overview' ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400 bg-blue-50/50 dark:bg-blue-950/20' : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-[#21262d]/50'}`}><T>Overview</T></button>
            <button onClick={() => setActiveTab("timesheets")} className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${activeTab === 'timesheets' ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400 bg-blue-50/50 dark:bg-blue-950/20' : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-[#21262d]/50'}`}><T>Daily Logs</T></button>
            <button onClick={() => setActiveTab("documents")} className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${activeTab === 'documents' ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400 bg-blue-50/50 dark:bg-blue-950/20' : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-[#21262d]/50'}`}><T>Documents </T><span className="bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-slate-200 px-1.5 py-0.5 rounded-full text-xs ml-1">{taskDocs.length}</span></button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
                {activeTab === "overview" && (
                    <div className="bg-white dark:bg-[#161b22] p-6 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d] animate-fade-in">
                        <div className="mb-6">
                            <h4 className="font-bold text-gray-800 dark:text-white mb-3 text-sm uppercase tracking-wide"><T>Task Description & Scope</T></h4>
                            <p className="text-gray-600 dark:text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">{task.description || "No additional description provided."}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4 border-t border-gray-100 dark:border-[#30363d] pt-6">
                            <div className="p-4 bg-gray-50 dark:bg-[#21262d] rounded-lg border border-gray-100 dark:border-[#30363d]">
                                <span className="text-xs text-gray-500 dark:text-slate-400 block mb-1 font-bold uppercase"><T>Start Date</T></span>
                                <span className="font-medium text-gray-800 dark:text-slate-200 flex items-center gap-2"><CalendarDaysIcon className="w-4 h-4 text-blue-500" /> {formatDate(task.startDate || task.start_date)}</span>
                            </div>
                            <div className="p-4 bg-gray-50 dark:bg-[#21262d] rounded-lg border border-gray-100 dark:border-[#30363d]">
                                <span className="text-xs text-gray-500 dark:text-slate-400 block mb-1 font-bold uppercase"><T>Due Date</T></span>
                                <span className="font-medium text-gray-800 dark:text-slate-200 flex items-center gap-2"><CalendarDaysIcon className="w-4 h-4 text-red-500" /> {formatDate(task.dueDate || task.due_date || task.endDate || task.end_date)}</span>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === "timesheets" && (
                    <div className="space-y-6 animate-fade-in">
                        
                        {/* 🟢 STRICT REQUIREMENT: LOG HOURS FORM ONLY SHOWS IF SOMEONE IS ASSIGNED */}
                        {!isTaskAssigned ? (
                          <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border-2 border-dashed border-amber-300 dark:border-amber-800/60 rounded-xl p-8 text-center shadow-xs">
                            <div className="w-14 h-14 bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 rounded-full flex items-center justify-center mx-auto mb-3 shadow-xs">
                              <UserCircleIcon className="w-8 h-8" />
                            </div>
                            <h4 className="text-base font-bold text-gray-900 dark:text-white mb-1.5">
                              <T>Hour Logging Restricted</T>
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-slate-300 max-w-md mx-auto mb-5 leading-relaxed">
                              <T>Work hours and labor costs can only be logged for tasks assigned to a team member. Please assign a team member to unlock the time log form.</T>
                            </p>
                            <button
                              onClick={() => navigate(`/admin/task/edit/${id}`)}
                              className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-lg shadow-md transition"
                            >
                              <UserCircleIcon className="w-4 h-4" /> <T>Assign Team Member to Task</T>
                            </button>
                          </div>
                        ) : (
                          <div className="bg-white dark:bg-[#161b22] p-6 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d]">
                              <div className="flex justify-between items-center mb-4">
                                <h4 className="font-bold text-gray-800 dark:text-white flex items-center gap-2">
                                  <ClockIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                  <T>Log Time & Labor Cost</T>
                                </h4>
                                <span className="text-xs bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/60 px-2.5 py-1 rounded-full font-bold">
                                  Assignee: {displayEmployeeName}
                                </span>
                              </div>

                              <form onSubmit={handleLogSubmit} className="flex flex-col md:flex-row gap-4 items-end">
                                  <div className="flex-1 w-full">
                                      <label className="text-xs font-bold text-gray-500 dark:text-slate-400 mb-1 block uppercase"><T>Work Description</T> <span className="text-red-500">*</span></label>
                                      <input type="text" className="w-full border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" placeholder="What specific task or milestone was completed?" value={logData.description} onChange={e => setLogData({...logData, description: e.target.value})} required />
                                  </div>
                                  <div className="w-48">
                                      <label className="text-xs font-bold text-gray-500 dark:text-slate-400 mb-1 block uppercase"><T>Cost Sheet</T></label>
                                      <select className="w-full border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white rounded-lg p-2.5 text-sm" value={logData.costSheetId} onChange={e => setLogData({...logData, costSheetId: e.target.value})}>
                                          <option value=""><T>-- Unbilled --</T></option>
                                          {projectSheets.map(sheet => (
                                              <option key={sheet.id} value={sheet.id}>{sheet.name}</option>
                                          ))}
                                      </select>
                                  </div>
                                  <div className="w-28">
                                      <label className="text-xs font-bold text-gray-500 dark:text-slate-400 mb-1 block uppercase"><T>Hours</T> <span className="text-red-500">*</span></label>
                                      <input type="number" step="0.5" className="w-full border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" placeholder="0" min="0.1" value={logData.hours} onChange={e => setLogData({...logData, hours: e.target.value})} required />
                                  </div>
                                  <CustomFieldRenderer module="tasks" customData={customData} onChange={setCustomData} />
                                  <button type="submit" className="bg-blue-600 text-white px-6 py-2.5 rounded-lg text-sm font-bold hover:bg-blue-700 transition shadow-md shrink-0"><T>Log Hours</T></button>
                              </form>

                              {/* Interconnectivity Badge inside form */}
                              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs bg-blue-50/70 dark:bg-blue-950/30 text-blue-900 dark:text-blue-300 p-3 rounded-lg border border-blue-100 dark:border-blue-900/50">
                                  <div className="flex items-center gap-2">
                                    <UserCircleIcon className="w-4 h-4 text-blue-600" />
                                    <span><T>Personnel: </T><strong>{displayEmployeeName}</strong> ({displayEmployeeRole})</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <CurrencyRupeeIcon className="w-4 h-4 text-blue-600" />
                                    <span><T>Base Rate: </T><strong><Price amount={hourlyRate} />/hr</strong></span>
                                  </div>
                                  {Number(logData.hours) > 0 && (
                                    <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-bold">
                                      <span>Auto-Calculated Cost: <Price amount={Number(logData.hours) * hourlyRate} /></span>
                                    </div>
                                  )}
                              </div>
                          </div>
                        )}

                        {/* Logs Table with Strict DD/MM/YYYY formatting */}
                        <div className="bg-white dark:bg-[#161b22] rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d] overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200 dark:border-[#30363d] flex justify-between items-center bg-gray-50/50 dark:bg-[#21262d]/50">
                              <h5 className="font-bold text-xs uppercase tracking-wider text-gray-700 dark:text-slate-300">
                                <T>Logged Hours History</T> ({logs.length})
                              </h5>
                              <span className="text-xs text-gray-500 dark:text-slate-400">Total Logged: <strong>{totalSpentHours}h</strong></span>
                            </div>
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-50 dark:bg-[#21262d] text-gray-500 dark:text-slate-400 uppercase text-xs font-bold border-b border-gray-200 dark:border-[#30363d]">
                                    <tr>
                                        <th className="px-6 py-3"><T>Date</T></th>
                                        <th className="px-6 py-3"><T>Team Member</T></th>
                                        <th className="px-6 py-3"><T>Description</T></th>
                                        <th className="px-6 py-3 text-right"><T>Hours</T></th>
                                        <th className="px-6 py-3 text-right"><T>Cost</T></th>
                                        <th className="px-4 py-3 text-center"><T>Action</T></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-[#30363d]">
                                    {logs.length === 0 ? (
                                        <tr><td colSpan="6" className="p-8 text-center text-gray-400 dark:text-slate-500 italic"><T>No time logs recorded yet for this task.</T></td></tr>
                                    ) : (
                                        logs.map((log) => (
                                            <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-[#21262d]/60 transition">
                                                <td className="px-6 py-4 font-medium text-gray-700 dark:text-slate-300">{formatDate(log.date)}</td>
                                                <td className="px-6 py-4 font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                                  <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 text-[10px] font-bold flex items-center justify-center">
                                                    {(log.employeeName || "U").charAt(0).toUpperCase()}
                                                  </div>
                                                  {log.employeeName}
                                                </td>
                                                <td className="px-6 py-4 text-gray-600 dark:text-slate-300">{log.description}</td>
                                                <td className="px-6 py-4 text-right font-bold text-gray-800 dark:text-white">{log.hours}h</td>
                                                <td className="px-6 py-4 text-right text-emerald-600 dark:text-emerald-400 font-bold bg-green-50/20 dark:bg-green-950/10"><Price amount={Number(log.cost || 0)} /></td>
                                                <td className="px-4 py-4 text-center">
                                                  <button 
                                                    onClick={() => deleteLog(log.id, id)}
                                                    title="Delete time log"
                                                    className="text-gray-400 hover:text-red-500 p-1.5 rounded transition inline-flex items-center"
                                                  >
                                                    <TrashIcon className="w-4 h-4" />
                                                  </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeTab === "documents" && (
                    <div className="bg-white dark:bg-[#161b22] p-6 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d] animate-fade-in">
                        <div className="flex justify-between items-center mb-6">
                            <h4 className="font-bold text-gray-800 dark:text-white text-base uppercase"><T>Attached Files</T></h4>
                            <label className="cursor-pointer bg-white dark:bg-[#21262d] border border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400 px-4 py-2 rounded-lg text-xs font-bold uppercase hover:bg-blue-50 dark:hover:bg-blue-950/40 flex items-center gap-2 transition">
                                <PaperClipIcon className="w-4 h-4" /> Upload File
                                <input type="file" className="hidden" onChange={handleFileUpload} />
                            </label>
                        </div>
                        {taskDocs.length === 0 ? (
                            <div className="text-center py-12 border-2 border-dashed border-gray-200 dark:border-[#30363d] rounded-lg">
                                <DocumentIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-slate-600 mb-2" />
                                <p className="text-gray-400 dark:text-slate-500 text-sm"><T>No documents attached.</T></p>
                            </div>
                        ) : (
                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {taskDocs.map(doc => (
                                    <li key={doc.id} className="p-3 border border-gray-200 dark:border-[#30363d] rounded-lg flex justify-between items-center hover:shadow-sm transition bg-white dark:bg-[#21262d]">
                                        <div className="flex items-center gap-3 overflow-hidden">
                                            <div className="w-10 h-10 bg-gray-100 dark:bg-slate-800 rounded flex flex-shrink-0 items-center justify-center text-gray-500 dark:text-slate-400"><DocumentIcon className="w-6 h-6" /></div>
                                            <div className="min-w-0"><p className="text-sm font-medium text-gray-800 dark:text-white truncate">{doc.name}</p><p className="text-xs text-gray-500 dark:text-slate-400">{formatDate(doc.uploadDate || doc.created_at)} • {doc.size}</p></div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <a href={`${API_BASE_URL}${doc.file_path}`} target="_blank" rel="noreferrer" className="text-xs bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 px-2 py-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40 font-medium"><T>View</T></a>
                                            <button onClick={() => deleteDocument(doc.id)} className="text-gray-400 hover:text-red-500 p-1 rounded transition"><TrashIcon className="w-4 h-4" /></button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            {/* Sidebar Column */}
            <div className="lg:col-span-1 space-y-6">
                
                {/* Interconnected Assignee Card */}
                <div className="bg-white dark:bg-[#161b22] p-6 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d]">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-bold text-gray-800 dark:text-white text-xs uppercase tracking-wide"><T>Assigned Personnel</T></h4>
                      <button 
                        onClick={() => navigate(`/admin/task/edit/${id}`)}
                        className="text-[11px] font-bold text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {isTaskAssigned ? "Edit" : "+ Assign"}
                      </button>
                    </div>

                    {isTaskAssigned ? (
                      <>
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-sm">
                              {displayEmployeeName.charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                                <p className="text-base font-bold text-gray-900 dark:text-white truncate">{displayEmployeeName}</p>
                                <p className="text-xs text-gray-500 dark:text-slate-400 font-medium truncate">{displayEmployeeRole}</p>
                                <span className="inline-block mt-1 text-[10px] bg-green-100 dark:bg-green-950/60 text-green-700 dark:text-green-400 px-2 py-0.5 rounded font-bold border border-green-200 dark:border-green-800/60">
                                  <T>Active</T>
                                </span>
                            </div>
                        </div>

                        {/* Contact details if available */}
                        {(displayEmployeePhone || displayEmployeeEmail) && (
                          <div className="mt-4 pt-3 border-t border-gray-100 dark:border-[#30363d] space-y-1.5 text-xs text-gray-600 dark:text-slate-400">
                            {displayEmployeePhone && <p className="truncate">📞 {displayEmployeePhone}</p>}
                            {displayEmployeeEmail && <p className="truncate">✉️ {displayEmployeeEmail}</p>}
                          </div>
                        )}

                        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-[#30363d] space-y-2 text-sm">
                            <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400"><T>Hourly Rate</T></span><span className="font-bold text-gray-900 dark:text-white"><Price amount={hourlyRate} />/hr</span></div>
                            <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400"><T>Est. Labor Cost</T></span><span className="font-semibold text-gray-800 dark:text-slate-200"><Price amount={estLaborCost} /></span></div>
                            <div className="flex justify-between text-emerald-600 dark:text-emerald-400 font-bold"><span><T>Incurred Labor</T></span><span><Price amount={incurredLaborCost} /></span></div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-6">
                        <div className="w-12 h-12 bg-amber-50 dark:bg-amber-950/40 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-2">
                          <UserCircleIcon className="w-7 h-7" />
                        </div>
                        <p className="font-bold text-gray-800 dark:text-white text-sm mb-1">No Team Member Assigned</p>
                        <p className="text-xs text-gray-400 mb-4">Assign someone to enable timesheet hour logging</p>
                        <button
                          onClick={() => navigate(`/admin/task/edit/${id}`)}
                          className="bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs uppercase px-4 py-2 rounded-lg transition"
                        >
                          + Assign Now
                        </button>
                      </div>
                    )}
                </div>

                {/* Work Details & BOQ Valuation Card */}
                <div className="bg-white dark:bg-[#161b22] p-6 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d]">
                    <h4 className="font-bold text-gray-800 dark:text-white mb-4 text-xs uppercase tracking-wide"><T>Measurement & BOQ</T></h4>
                    <div className="space-y-3">
                        <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-slate-400"><T>Quantity</T></span><span className="font-medium text-gray-800 dark:text-white">{task.quantity || "—"} {task.unit || ""}</span></div>
                        <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-slate-400"><T>Unit Rate</T></span><span className="font-medium text-gray-800 dark:text-white"><Price amount={Number(task.unitRate || 0)} /></span></div>
                        <div className="pt-3 border-t border-gray-100 dark:border-[#30363d] flex justify-between text-sm font-bold text-purple-700 dark:text-purple-400">
                          <span><T>Total Work Value</T></span>
                          <span><Price amount={totalTaskValue} /></span>
                        </div>
                    </div>
                </div>

                {/* Project Context Quick Card */}
                <div className="bg-white dark:bg-[#161b22] p-6 rounded-xl shadow-sm border border-gray-200 dark:border-[#30363d]">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-bold text-gray-800 dark:text-white text-xs uppercase tracking-wide"><T>Project Context</T></h4>
                      {projectId && (
                        <button 
                          onClick={() => navigate(`/admin/project/${projectId}`)} 
                          className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline font-bold"
                        >
                          Open Project →
                        </button>
                      )}
                    </div>
                    <p className="font-bold text-gray-900 dark:text-white text-sm mb-1">{displayProjectName}</p>
                    {displayProjectLocation && <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">📍 {displayProjectLocation}</p>}
                    {displayProjectStage && <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Stage: {displayProjectStage}</p>}
                </div>

            </div>
        </div>
      </div>
    </>
  );
}